import { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { vacationApi, remoteApi, personalDaysApi } from '../utils/api';
import {
  Palmtree, Laptop, Loader, CheckCircle, XCircle, Check, X,
  Clock, CalendarDays, AlertTriangle, Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const periodLabels = { morning: 'Mañana', afternoon: 'Tarde', full_day: 'Día completo' };

export default function ApprovalsPage() {
  const { t } = useLanguage();
  const [vacations, setVacations] = useState([]);
  const [remotes, setRemotes] = useState([]);
  const [cancelVacations, setCancelVacations] = useState([]);
  const [cancelRemotes, setCancelRemotes] = useState([]);
  const [personalDays, setPersonalDays] = useState([]);
  const [annualPlans, setAnnualPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [actionDialog, setActionDialog] = useState(null); // { type, id, action, category }
  const [comment, setComment] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vacRes, remRes, cancelVacRes, cancelRemRes, plansRes, pdRes] = await Promise.all([
        vacationApi.list({ status: 'pending' }),
        remoteApi.list({ status: 'pending' }),
        vacationApi.list({ status: 'cancellation_requested' }),
        remoteApi.list({ status: 'cancellation_requested' }),
        remoteApi.getAnnualPlan(new Date().getFullYear()),
        personalDaysApi.list({ status: 'pending' }).catch(() => ({ data: [] })),
      ]);
      setVacations(vacRes.data);
      setRemotes(remRes.data);
      setCancelVacations(cancelVacRes.data);
      setCancelRemotes(cancelRemRes.data);
      setPersonalDays(pdRes.data || []);
      setAnnualPlans((plansRes.data || []).filter(p => p.status === 'pending'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    const { type, id, action, category } = actionDialog;
    setProcessing(p => ({ ...p, [`${id}-${action}`]: true }));
    try {
      if (category === 'new') {
        if (action === 'approve') {
          if (type === 'vacation') await vacationApi.approve(id, { comment });
          else await remoteApi.approve(id, { comment });
        } else {
          if (type === 'vacation') await vacationApi.reject(id, { comment });
          else await remoteApi.reject(id, { comment });
        }
      } else if (category === 'cancel') {
        if (action === 'approve') {
          if (type === 'vacation') await vacationApi.approveCancel(id, { comment });
          else await remoteApi.approveCancel(id, { comment });
        } else {
          if (type === 'vacation') await vacationApi.rejectCancel(id, { comment });
          else await remoteApi.rejectCancel(id, { comment });
        }
      } else if (category === 'annual') {
        if (action === 'approve') await remoteApi.approveAnnualPlan(id, { comment });
        else await remoteApi.rejectAnnualPlan(id, { comment });
      } else if (category === 'personal') {
        if (action === 'approve') await personalDaysApi.approve(id, comment);
        else await personalDaysApi.reject(id, comment);
      }
      await loadData();
    } catch (e) { console.error(e); }
    finally {
      setProcessing(p => ({ ...p, [`${id}-${action}`]: false }));
      setActionDialog(null);
      setComment('');
    }
  };

  const openDialog = (type, id, action, category) => {
    setActionDialog({ type, id, action, category });
    setComment('');
  };

  const totalNew = vacations.length + remotes.length + personalDays.length;
  const totalCancel = cancelVacations.length + cancelRemotes.length;
  const totalAnnual = annualPlans.length;
  const totalPending = totalNew + totalCancel + totalAnnual;

  const matchSearch = (name) => !search || name?.toLowerCase().includes(search.toLowerCase());

  const ActionButtons = ({ type, id, category, approveLabel = 'Aprobar', rejectLabel = 'Rechazar' }) => (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <Button
        size="sm"
        onClick={() => openDialog(type, id, 'approve', category)}
        disabled={!!processing[`${id}-approve`]}
        data-testid={`btn-approve-${id}`}
        className="h-8 gap-1.5 bg-brand hover:bg-brand-hover text-white"
      >
        {processing[`${id}-approve`] ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        {approveLabel}
      </Button>
      <Button
        size="sm" variant="outline"
        onClick={() => openDialog(type, id, 'reject', category)}
        disabled={!!processing[`${id}-reject`]}
        data-testid={`btn-reject-${id}`}
        className="h-8 gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
      >
        <X className="h-3.5 w-3.5" />{rejectLabel}
      </Button>
    </div>
  );

  // Card component for new requests
  const NewRequestCard = ({ req, type }) => {
    if (!matchSearch(req.user_name)) return null;
    const Icon = type === 'vacation' ? Palmtree : Laptop;
    const bg = type === 'vacation' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30';
    const ic = type === 'vacation' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400';
    return (
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`approval-card-${req.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`h-5 w-5 ${ic}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm font-heading">{req.user_name}</span>
                <span className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString('es-ES')}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {type === 'vacation'
                  ? <>{req.start_date} → {req.end_date} <span className="font-medium text-foreground">({req.working_days} días)</span></>
                  : <>{req.date} · <span className="font-medium text-foreground">{periodLabels[req.period_type]}</span> · {req.days_count}d</>
                }
              </div>
              {req.notes && <div className="text-xs text-muted-foreground italic mt-0.5">"{req.notes}"</div>}
            </div>
          </div>
          <ActionButtons type={type} id={req.id} category="new" />
        </div>
      </div>
    );
  };

  // Card component for cancellation requests
  const CancelCard = ({ req, type }) => {
    if (!matchSearch(req.user_name)) return null;
    const Icon = type === 'vacation' ? Palmtree : Laptop;
    return (
      <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`cancel-card-${req.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0">
              <Icon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm font-heading">{req.user_name}</span>
                <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full font-medium">Solicitud de cancelación</span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {type === 'vacation'
                  ? <>{req.start_date} → {req.end_date} · {req.working_days} días</>
                  : <>{req.date} · {periodLabels[req.period_type]}</>
                }
              </div>
              {req.cancel_reason && (
                <div className="text-xs text-orange-700 dark:text-orange-300 mt-1 italic">
                  Motivo: "{req.cancel_reason}"
                </div>
              )}
            </div>
          </div>
          <ActionButtons type={type} id={req.id} category="cancel" approveLabel="Aprobar" rejectLabel="Denegar" />
        </div>
      </div>
    );
  };

  // Annual plan card
  const AnnualPlanCard = ({ plan }) => {
    if (!matchSearch(plan.user_name)) return null;
    return (
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`annual-plan-card-${plan.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm font-heading">{plan.user_name}</span>
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">Plan anual {plan.year}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">{plan.planned_days?.length} días planificados</div>
              {plan.planned_days?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {plan.planned_days.slice(0, 6).map(d => (
                    <span key={d} className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-lg">{d}</span>
                  ))}
                </div>
              )}
              {plan.notes && <div className="text-xs text-muted-foreground italic mt-1">"{plan.notes}"</div>}
            </div>
          </div>
          <ActionButtons type="annual" id={plan.id} category="annual" />
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="h-6 w-6 animate-spin text-brand" />
    </div>
  );

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-heading">{t('approvals')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {totalPending > 0 ? `${totalPending} elemento(s) pendiente(s)` : 'Sin elementos pendientes'}
          </p>
        </div>
        <Input
          placeholder="Buscar por empleado..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-48 h-8 text-sm"
          data-testid="search-approvals"
        />
      </div>

      {totalPending === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
          <CheckCircle className="h-12 w-12 text-brand mx-auto mb-3 opacity-60" />
          <h2 className="font-semibold font-heading text-lg mb-1">Todo al día</h2>
          <p className="text-sm text-muted-foreground">No hay solicitudes pendientes de aprobación</p>
        </div>
      ) : (
        <Tabs defaultValue="new" className="space-y-4">
          <TabsList>
            <TabsTrigger value="new" data-testid="tab-new-requests">
              Nuevas {totalNew > 0 && <span className="ml-1.5 bg-brand text-white text-xs px-1.5 py-0.5 rounded-full">{totalNew}</span>}
            </TabsTrigger>
            <TabsTrigger value="cancellations" data-testid="tab-cancellations">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              Cancelaciones {totalCancel > 0 && <span className="ml-1.5 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{totalCancel}</span>}
            </TabsTrigger>
            <TabsTrigger value="annual" data-testid="tab-annual-plans">
              <CalendarDays className="h-3.5 w-3.5 mr-1" />
              Planes Anuales {totalAnnual > 0 && <span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">{totalAnnual}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <div className="space-y-3">
              {totalNew === 0
                ? <div className="text-center py-8 text-muted-foreground text-sm">No hay nuevas solicitudes</div>
                : (
                  <>
                    {[...vacations.map(v => ({ ...v, _t: 'vacation' })), ...remotes.map(r => ({ ...r, _t: 'remote' }))]
                      .sort((a, b) => a.created_at?.localeCompare(b.created_at))
                      .map(req => <NewRequestCard key={req.id} req={req} type={req._t} />)
                    }
                    {personalDays.map(pd => matchSearch(pd.user_name) && (
                      <div key={pd.id} className="bg-amber-50/40 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`personal-card-${pd.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                              <Coffee className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm font-heading">{pd.user_name}</span>
                                <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full font-medium">Asunto propio</span>
                              </div>
                              <div className="text-sm text-muted-foreground mt-0.5">{pd.date}{pd.convenio_name && ` · ${pd.convenio_name}`}</div>
                              {pd.notes && <div className="text-xs text-muted-foreground italic mt-1">"{pd.notes}"</div>}
                            </div>
                          </div>
                          <ActionButtons type="personal" id={pd.id} category="personal" />
                        </div>
                      </div>
                    ))}
                  </>
                )
              }
            </div>
          </TabsContent>

          <TabsContent value="cancellations">
            <div className="space-y-3">
              {totalCancel === 0
                ? <div className="text-center py-8 text-muted-foreground text-sm">No hay solicitudes de cancelación</div>
                : [...cancelVacations.map(v => ({ ...v, _t: 'vacation' })), ...cancelRemotes.map(r => ({ ...r, _t: 'remote' }))]
                    .sort((a, b) => a.updated_at?.localeCompare(b.updated_at))
                    .map(req => <CancelCard key={req.id} req={req} type={req._t} />)
              }
            </div>
          </TabsContent>

          <TabsContent value="annual">
            <div className="space-y-3">
              {totalAnnual === 0
                ? <div className="text-center py-8 text-muted-foreground text-sm">No hay planes anuales pendientes</div>
                : annualPlans.map(plan => <AnnualPlanCard key={plan.id} plan={plan} />)
              }
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Action dialog (approve/reject) */}
      <Dialog open={!!actionDialog} onOpenChange={() => { setActionDialog(null); setComment(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.action === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Comentario {actionDialog?.action === 'reject' ? '(requerido)' : '(opcional)'}</Label>
            <Textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={actionDialog?.action === 'approve' ? 'Añade un comentario (opcional)...' : 'Explica el motivo...'}
              rows={3}
              data-testid="action-comment"
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Cancelar</Button>
            {actionDialog?.action === 'approve' ? (
              <Button onClick={handleAction} data-testid="confirm-approve" className="bg-brand hover:bg-brand-hover text-white gap-2">
                <CheckCircle className="h-4 w-4" />Confirmar aprobación
              </Button>
            ) : (
              <Button onClick={handleAction} variant="destructive" data-testid="confirm-reject" className="gap-2"
                disabled={!comment.trim() && actionDialog?.category !== 'cancel'}>
                <XCircle className="h-4 w-4" />Confirmar rechazo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
